import Config from "../../components/common/Config";

/**
 * Platform constants for cart operations
 */
export const PLATFORMS = {
    TELE_CALLING: "5",
    POS: "4",
    WEBSITE: "2",
};

export const resolveCartChannelId = (platform) => {
    if (Config.PREFIX === "atr") {
        const role =
            localStorage.getItem("user_role") ||
            localStorage.getItem("active_role_id");
        if (role === "8") {
            return PLATFORMS.WEBSITE;
        }
    }
    if (platform !== undefined && platform !== null && String(platform).trim() !== "") {
        return String(platform);
    }
    return PLATFORMS.TELE_CALLING;
};

const EPOS_CUSTOMER_STORAGE_KEY = "epos_selectedCustomer";
const QUICK_ORDER_CUSTOMER_STORAGE_KEY = "quickOrder_selectedCustomer";

const resolvePlatformFromRole = () => {
    try {
        const activeRoleId =
            localStorage.getItem("active_role_id") ||
            localStorage.getItem("user_role");
        if (activeRoleId === "18" || activeRoleId === 18) return PLATFORMS.POS;
        if (activeRoleId === "20" || activeRoleId === 20) return PLATFORMS.TELE_CALLING;

        const userRolesData = localStorage.getItem("user_roles_data");
        if (activeRoleId && userRolesData) {
            const roles = JSON.parse(userRolesData);
            const activeRole = roles.find((role) => String(role.id) === String(activeRoleId));
            if (activeRole) {
                const name = (activeRole.name || "").toLowerCase();
                if (name === "till" || name.includes("cashier")) return PLATFORMS.POS;
                if (name.includes("tele calling") || name.includes("telecaller")) {
                    return PLATFORMS.TELE_CALLING;
                }
            }
        }
    } catch (_) {
        // ignore parse errors
    }
    return null;
};

const isEposSalesPath = (path) =>
    /\/sales\/epos(\/|$)/.test(path) || /\/sales\/epos_sales(\/|$)/.test(path);

/**
 * Resolve cart platform from explicit props, route, active role, or customer storage.
 * @returns {string|null} "4" (POS), "5" (TELE_CALLING), or null if undetermined
 */
export const resolveCartPlatform = ({ platform, isEpos } = {}) => {
    if (platform !== undefined && platform !== null && String(platform).trim() !== "") {
        return String(platform);
    }
    if (isEpos === true) return PLATFORMS.POS;
    if (isEpos === false) return PLATFORMS.TELE_CALLING;

    if (typeof window === "undefined") return null;

    const path = window.location.pathname || "";
    if (isEposSalesPath(path)) return PLATFORMS.POS;
    if (path.includes("/sales/quick-order")) return PLATFORMS.TELE_CALLING;

    const rolePlatform = resolvePlatformFromRole();
    if (rolePlatform) return rolePlatform;

    try {
        const eposRaw = localStorage.getItem(EPOS_CUSTOMER_STORAGE_KEY);
        const qoRaw = sessionStorage.getItem(QUICK_ORDER_CUSTOMER_STORAGE_KEY);
        let eposId = null;
        let qoId = null;
        if (eposRaw) {
            const parsed = JSON.parse(eposRaw);
            eposId = parsed?.id ? String(parsed.id) : null;
        }
        if (qoRaw) {
            const parsed = JSON.parse(qoRaw);
            qoId = parsed?.id ? String(parsed.id) : null;
        }
        if (eposId && !qoId) return PLATFORMS.POS;
        if (qoId && !eposId) return PLATFORMS.TELE_CALLING;
    } catch (_) {
        // ignore parse errors
    }

    return null;
};

export const resolveCartPlatformFromRoleProfile = ({
    roleName = "",
    hasPOSPermission = false,
    hasSalesQuickOrderPermission = false,
} = {}) => {
    if (hasPOSPermission && !hasSalesQuickOrderPermission) {
        return PLATFORMS.POS;
    }
    if (hasSalesQuickOrderPermission && !hasPOSPermission) {
        return PLATFORMS.TELE_CALLING;
    }
    if (hasPOSPermission && hasSalesQuickOrderPermission) {
        const name = String(roleName || "").toLowerCase();
        if (name.includes("till") || name.includes("cashier")) {
            return PLATFORMS.POS;
        }
        if (name.includes("tele calling") || name.includes("telecaller")) {
            return PLATFORMS.TELE_CALLING;
        }
    }
    return null;
};

export const getCustomerStorageKeyForPlatform = (platform) =>
    platform === PLATFORMS.POS ? EPOS_CUSTOMER_STORAGE_KEY : QUICK_ORDER_CUSTOMER_STORAGE_KEY;

export const getSelectedCustomerFromStorage = (platform) => {
    if (!platform) return null;
    try {
        const storage = platform === PLATFORMS.POS ? localStorage : sessionStorage;
        const raw = storage.getItem(getCustomerStorageKeyForPlatform(platform));
        if (!raw) return null;
        const customer = JSON.parse(raw);
        return customer?.id ? customer : null;
    } catch (_) {
        return null;
    }
};

const getWsAddToCartErrorMessage = (data) => {
    if (!data || typeof data !== "object") return null;
    const nested = data.data;
    const fromNested = nested && typeof nested === "object"
        ? (nested.message || nested.error || nested.msg)
        : null;
    return data.message || data.error || data.msg || fromNested || null;
};

/**
 * Add item to cart
 * @param {Object} data - Cart item data
 * @param {string} platform - Platform identifier ("5" for TELE_CALLING or "4" for POS). Defaults to "5"
 * @returns {Promise<Object>} API response
 */
export const addToCart = async (data, platform = PLATFORMS.TELE_CALLING) => {
    if (!data.customer_id) {
        throw new Error("Customer ID is required");
    }
    const customerIdInt = parseInt(data.customer_id, 10);
    if (!Number.isInteger(customerIdInt) || customerIdInt <= 0) {
        throw new Error("Invalid customer ID");
    }
    const accessToken = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("id", data.id);
    formData.append("customer_id", customerIdInt);
    formData.append("product_id", data.product_id);
    formData.append("qty", data.qty);
    formData.append("price", data.price);
    formData.append("piece_type", data.piece_type);
    formData.append("sub_total", data.sub_total);
    formData.append("is_contra", data.is_contra);
    formData.append(
        "is_refund",
        data.is_refund !== undefined && data.is_refund !== null && data.is_refund !== ""
            ? String(data.is_refund).trim()
            : "0"
    );
    formData.append("promotion_id", data.promotion_id || "");
    formData.append("is_promoted", data.is_promoted);
    const channelId = resolveCartChannelId(platform);
    formData.append("platform", channelId);
    formData.append("channel_id", channelId);
    // Always append discount_type and discount_value, even if they're default values
    const discountType = data.discount_type !== undefined && data.discount_type !== null && data.discount_type !== ""
        ? String(data.discount_type).trim()
        : "percentage";
    const discountValue = data.discount_value !== undefined && data.discount_value !== null && data.discount_value !== ""
        ? String(data.discount_value).trim()
        : "0.00";
    formData.append("discount_type", discountType);
    formData.append("discount_value", discountValue);

    if (data.discount_comment !== undefined && data.discount_comment !== null) {
        formData.append("discount_comment", String(data.discount_comment));
    }

    if (data.price_brand !== undefined && data.price_brand !== null && String(data.price_brand).trim() !== "") {
        formData.append("price_brand", String(data.price_brand).trim());
    }
    
    if (data.selected_free_product_id !== undefined && data.selected_free_product_id !== null) {
        formData.append("selected_free_product_id", String(data.selected_free_product_id));
    }
    if (data.selected_PromoID !== undefined && data.selected_PromoID !== null) {
        formData.append("selected_PromoID", String(data.selected_PromoID));
    }
    if (data.selected_free_product_piece_type !== undefined && data.selected_free_product_piece_type !== null) {
        formData.append("selected_free_product_piece_type", String(data.selected_free_product_piece_type));
    }

    const response = await fetch(`${Config.API_URL}/ws-add-to-cart`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${accessToken}`,
        },
        body: formData,
    });
    const responseText = await response.text();
    let responseData = null;
    try {
        responseData = responseText ? JSON.parse(responseText) : null;
    } catch {
        responseData = null;
    }
    if (!response.ok) {
        const msg =
            getWsAddToCartErrorMessage(responseData) ||
            `HTTP error! status: ${response.status}`;
        throw new Error(String(msg));
    }
    if (responseData && responseData.success === false) {
        throw new Error(
            getWsAddToCartErrorMessage(responseData) || "Failed to add product to cart"
        );
    }
    return responseData;
}

/**
 * Get cart list for a customer
 * @param {number|string} customerId - Customer ID
 * @param {string} platform - Platform identifier ("5" for TELE_CALLING or "4" for POS). Defaults to "5"
 * @returns {Promise<Object>} API response
 */
export const getCartList = async (customerId, platform = PLATFORMS.TELE_CALLING) => {
    if (!customerId) {
        throw new Error("Customer ID is required");
    }
    const customerIdInt = parseInt(customerId, 10);
    if (!Number.isInteger(customerIdInt) || customerIdInt <= 0) {
        throw new Error("Invalid customer ID");
    }
    const accessToken = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("customer_id", customerIdInt);
    const channelId = resolveCartChannelId(platform);
    formData.append("platform", channelId);
    formData.append("channel_id", channelId);
    const response = await fetch(`${Config.API_URL}/ws-cart-list`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${accessToken}`,
        },
        body: formData,
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const responseData = await response.json();
    return responseData;
}
// {
//     "data": [
//       {
//         "product_id": "50",
//         "name": "KETTLE CHEESE AND ONION",
//         "code": "0002",
//         "size": "12 X 130g",
//         "sales_box_price": "13.42",
//         "sales_piece_price": "1.38",
//         "offer_box_price": "0.00",
//         "offer_piece_price": "0.00",
//         "price": "13.42",
//         "vat": "0.67",
//         "tax": "0.67",
//         "qty": "1",
//         "sub_total": "13.42",
//         "order_type": "Box",
//         "is_promoted": "0",
//         "promo_id": "24",
//         "promo_type": "BUY",
//         "id": "49",
//         "isContractPriceApplied": "N",
//         "image": "0002.jpg"
//       }
//     ],
//     "success": true,
//     "message": "Product list"
//   }

export const clearCartByCustomer = async (customerId, platform = PLATFORMS.TELE_CALLING, promotionId = null) => {
    try {
        if (!customerId) {
            throw new Error("Customer ID is required");
        }
        const accessToken = localStorage.getItem("token");
        const formData = new FormData();
        const channelId = resolveCartChannelId(platform);
        formData.append("platform", channelId);
        formData.append("channel_id", channelId);
        formData.append("customer_id", customerId);
        const promotionIdStr =
            promotionId !== undefined && promotionId !== null && promotionId !== ""
                ? String(promotionId)
                : "";
        formData.append("promotion_id", promotionIdStr);

        const response = await fetch(`${Config.API_URL}/ws-pos-clear-cart`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error("Clear cart error:", error);
        throw error;
    }
}

export const clearCart = async (payload = null, platformArg = PLATFORMS.TELE_CALLING) => {
    try {
        const accessToken = localStorage.getItem("token");
        let body;
        let customerId = payload;
        let platform = platformArg;

        // Support both signatures:
        // 1) clearCart(customerId, platform)
        // 2) clearCart({ customerId, platform })
        if (payload && typeof payload === "object" && !Array.isArray(payload)) {
            customerId = payload.customerId ?? payload.customer_id ?? payload.id ?? null;
            platform = payload.platform ?? PLATFORMS.TELE_CALLING;
        }

        if (customerId && typeof customerId === "object") {
            customerId = customerId.id ?? customerId.customerId ?? customerId.customer_id ?? null;
        }

        const normalizedPlatform = resolveCartChannelId(platform);

        if (customerId) {
            const formData = new FormData();
            formData.append("customer_id", String(customerId));
            formData.append("platform", normalizedPlatform);
            formData.append("channel_id", normalizedPlatform);
            body = formData;
        }

        const response = await fetch(`${Config.API_URL}/ws-clear-cart-list`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "Accept": "application/json",
            },
            body,
        });
        if (!response.ok) {
            if (response.status === 500) {
                throw new Error("Internal Server Error (500)");
            }
            throw new Error(`Request failed with status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error(error);
        throw error;
    }
};
/**
 * Delete a specific cart item
 * @param {Object} values - Cart item data (must contain id)
 * @param {number|string} customerId - Customer ID
 * @param {string} platform - Platform identifier ("5" for TELE_CALLING or "4" for POS). Defaults to "5"
 * @returns {Promise<Object>} API response
 */
export const deleteSpecificCartItem = async (values, customerId, platform = PLATFORMS.TELE_CALLING) => {
    try {
        if (!customerId) {
            throw new Error("Customer ID is required");
        }
        const customerIdInt = parseInt(customerId, 10);
        if (!Number.isInteger(customerIdInt) || customerIdInt <= 0) {
            throw new Error("Invalid customer ID");
        }
        const accessToken = localStorage.getItem("token");
        const formData = new FormData();
        formData.append("id", values.id);
        formData.append("customer_id", customerIdInt);
        const channelId = resolveCartChannelId(platform);
        formData.append("platform", channelId);
        formData.append("channel_id", channelId);
        const response = await fetch(`${Config.API_URL}/ws-clear-cart-by-id`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "Accept": "application/json",
            },
            body: formData,
        });
        if (!response.ok) {
            if (response.status === 500) {
                throw new Error("Internal Server Error (500)");
            }
            throw new Error(`Request failed with status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error(error);
        throw error;
    }
};
// ws-remove-promo-products-from-cart
// customer_id :
// platform :
// promotion_id
export const remove_promotions = async (values, customerId, platform = PLATFORMS.TELE_CALLING, promotion_id) => {
    try {
        if (!customerId) {
            throw new Error("Customer ID is required");
        }
        const customerIdInt = parseInt(customerId, 10);
        if (!Number.isInteger(customerIdInt) || customerIdInt <= 0) {
            throw new Error("Invalid customer ID");
        }
        const accessToken = localStorage.getItem("token");
        const formData = new FormData();
        formData.append("customer_id", customerIdInt);
        const channelId = resolveCartChannelId(platform);
        formData.append("platform", channelId);
        formData.append("channel_id", channelId);
        formData.append("promotion_id", promotion_id);
        const response = await fetch(`${Config.API_URL}/ws-remove-promo-products-from-cart`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "Accept": "application/json",
            },
            body: formData,
        });
        if (!response.ok) {
            if (response.status === 500) {
                throw new Error("Internal Server Error (500)");
            }
            throw new Error(`Request failed with status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error(error);
        throw error;
    }
};